"use client";

import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { sellAction } from "@/app/actions/leads";

// Identity + short-answer fields. `description` is rendered separately as a
// textarea below the grid.
const FIELDS = [
  { name: "name", label: "Name", placeholder: "Your name", required: true },
  { name: "email", label: "Email", placeholder: "you@company.com", type: "email", required: true },
  { name: "phone", label: "Phone", placeholder: "(555) 555-5555", required: false },
  { name: "company", label: "Company", placeholder: "Business or store name", required: false },
  { name: "product", label: "What are you selling?", placeholder: "Category, brand, SKUs", required: true },
  { name: "quantity", label: "How much?", placeholder: "Volume, units, or cases", required: false },
  { name: "price", label: "Asking price", placeholder: "Optional", required: false },
] as const;

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className="group w-full flex items-center justify-center gap-2 py-4 bg-cl-blue text-cl-bg font-display font-bold text-sm rounded hover:bg-cl-blue/90 transition-colors disabled:opacity-40"
    >
      {pending ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          Submit Inventory{" "}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </>
      )}
    </button>
  );
}

export function SellForm() {
  const [state, formAction] = useActionState(sellAction, undefined);

  if (state?.ok) {
    return (
      <div className="min-h-screen bg-cl-bg flex items-center justify-center text-center px-4">
        <div>
          <Check className="w-12 h-12 text-cl-green mx-auto mb-4" />
          <h1 className="font-display font-bold text-3xl text-cl-text mb-3">
            Got It
          </h1>
          <p className="text-cl-muted text-sm">
            Thanks for the pitch. Our team will reach out within 24 hours.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cl-bg">
      <div className="max-w-xl mx-auto px-4 lg:px-8 pt-32 pb-24">
        <p className="text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-4">
          Sell With Us
        </p>
        <h1 className="font-display font-bold text-3xl text-cl-text mb-3">
          Move Your Inventory
        </h1>
        <p className="text-cl-muted text-sm mb-10">
          Tell us what you have and how much of it. We'll place it with the right
          wholesale buyers.
        </p>
        <form action={formAction} className="space-y-8">
          {FIELDS.map((field) => (
            <div key={field.name}>
              <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
                {field.label}
              </label>
              <input
                type={"type" in field ? field.type : "text"}
                name={field.name}
                placeholder={field.placeholder}
                required={field.required}
                className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/60 focus:border-cl-blue focus:outline-none transition-colors"
              />
            </div>
          ))}
          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Description
            </label>
            <textarea
              name="description"
              required
              rows={4}
              placeholder="Condition, packaging, timeline, anything else we should know..."
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/60 focus:border-cl-blue focus:outline-none transition-colors resize-none"
            />
          </div>
          {state?.error ? (
            <p className="text-red-400 text-sm font-mono">{state.error}</p>
          ) : null}
          <SubmitButton />
        </form>
      </div>
    </div>
  );
}
